import { create } from 'zustand';
import { useChaosStore } from '../stores/chaos';

type Theme = 'light' | 'dark' | 'system';

interface ThemeStore {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (newTheme: Theme) => void;
  detectSystemTheme: () => void;
}

const storedTheme = typeof window !== 'undefined' ? (localStorage.getItem('theme') as Theme) : null;

export const useThemeStore = create<ThemeStore>((set) => ({
  theme: storedTheme ?? 'system', // Default to system preference
  resolvedTheme: 'light', // Actual theme applied
  toggleTheme: () =>
    set((state) => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      return {
        theme: newTheme,
        resolvedTheme: newTheme,
      };
    }),
  setTheme: (newTheme) => {
    set((state) => {
      if (newTheme === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        return {
          theme: 'system',
          resolvedTheme: systemDark ? 'dark' : 'light',
        };
      }
      localStorage.setItem('theme', newTheme);
      return { theme: newTheme, resolvedTheme: newTheme };
    });
  },
  detectSystemTheme: () => {
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    set({
      resolvedTheme: systemDark ? 'dark' : 'light',
    });
  },
}));

let chaosInterval: ReturnType<typeof setInterval> | null = null;

const getRandomTheme = () => (Math.random() > 0.5 ? 'light' : 'dark');

// Subscribe to chaos mode changes
useChaosStore.subscribe(
  (state) => {
    const mode = state.mode;
    if (chaosInterval) {
      clearInterval(chaosInterval);
      chaosInterval = null;
    }
    if (mode !== 'disabled') {
      chaosInterval = setInterval(() => {
        console.log('Triggering theme change');
        const newTheme = getRandomTheme();
        // Update theme in store
        useThemeStore.setState({
          theme: newTheme,
          resolvedTheme: newTheme,
        });
        localStorage.setItem('theme', newTheme);
      }, 10000);
    }
  }
);
